import {
  useCallback, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from '~/_shared/utils/hooks';
import {
  createDisplayName, MapSearchFilterBy,
} from '~/map/filter/mapSearchFilterBy.enum';
import { SettingsAccordionDropdownComponent } from '~/map/settings/accordion/settingsAccordionDropdown.component';
import { SettingsAccordionDropDownOptionComponent } from '~/map/settings/accordion/settingsAccordionDropdownOption.component';
import { publicMapSettingsSetSearchDefaultTool } from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.actionCreators';
import { usePublicMapSettingsSearchFilterDefaultOption } from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.selectors';

export const useSharedPresentationalMapsSearchFilterDropdown = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const searchFilterDefaultOption = usePublicMapSettingsSearchFilterDefaultOption();

  const onSharedMapSearchFilterDefaultOptionChange = useCallback((filterBy: MapSearchFilterBy) => () => (
    dispatch(publicMapSettingsSetSearchDefaultTool(filterBy))
  ), [dispatch]);

  const onDefaultSearchFilterHeaderClick = useCallback(() => {
    setIsDropdownOpen(isOpen => !isOpen);
  }, []);

  return useMemo(() => {
    const filterByValues = Object.values(MapSearchFilterBy)
      .map(value => ({ value, name: createDisplayName(value, t) }))
      // places is not enabled for presentational maps at this time
      .filter(filterBy => filterBy.value !== MapSearchFilterBy.PlacesAndPoi);

    const renderedOptions = filterByValues
      .map((filterBy, index) => (
        <SettingsAccordionDropDownOptionComponent
          key={filterBy.value}
          isLast={index === filterByValues.length - 1}
          title={filterBy.name}
          onChange={onSharedMapSearchFilterDefaultOptionChange(filterBy.value)}
        />
      ));

    const defaultSearchFilterLabel = createDisplayName(searchFilterDefaultOption, t);
    const triggerComponent = (
      <SettingsAccordionDropdownComponent
        isOpen={isDropdownOpen}
        onClick={onDefaultSearchFilterHeaderClick}
        triggerText={defaultSearchFilterLabel}
      />
    );

    return {
      isOpen: isDropdownOpen,
      onClose: () => setIsDropdownOpen(false),
      renderedOptions,
      triggerComponent,
      value: searchFilterDefaultOption,
    };
  }, [searchFilterDefaultOption, t, isDropdownOpen, onDefaultSearchFilterHeaderClick,
    onSharedMapSearchFilterDefaultOptionChange]);
};
